import React, { useState, useMemo, useRef, useCallback, useLayoutEffect } from 'react'
import { createPortal } from 'react-dom'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { apiService } from '../../services/api'
import LoadingSpinner from '../common/LoadingSpinner'
import Modal from '../common/Modal'
import { toast } from 'react-hot-toast'
import { NSModal, NSField, NSChip, NSSelect } from '../shared/ns-kit'
import { I, Icon } from '../shared/ns-icons'
import { nsConfirm } from '../shared/ns-confirm'

// ── Helpers ────────────────────────────────────────────────────────────────
const gradIndex = (id) =>
  ((id || '').toString().split('').reduce((a, c) => a + c.charCodeAt(0), 0) % 6) + 1

const getInitials = (name = '') =>
  name.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase() || '?'

const roleLabel = (role) => role ? role.charAt(0).toUpperCase() + role.slice(1) : 'Client'

const relativeTime = (d) => {
  if (!d) return '—'
  const diff = Date.now() - new Date(d).getTime()
  if (Number.isNaN(diff)) return '—'
  const m = Math.floor(diff / 60000)
  if (m < 1) return 'just now'
  if (m < 60) return `${m}m ago`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h}h ago`
  const days = Math.floor(h / 24)
  if (days === 1) return 'yesterday'
  if (days < 30) return `${days}d ago`
  const mo = Math.floor(days / 30)
  if (mo < 12) return `${mo}mo ago`
  return `${Math.floor(days / 365)}y ago`
}

// Small popover menu shell (used by the filter buttons and the row ⋯ menu).
// The menu is rendered in a portal with fixed positioning so it escapes the
// table's `overflow: hidden` (otherwise the dropdown is clipped inside the row).
function Popover({ trigger, align = 'left', width = 180, children }) {
  const [open, setOpen] = useState(false)
  const [pos, setPos] = useState(null) // null until measured, so we never flash at 0,0
  const anchorRef = useRef(null)
  const menuRef = useRef(null)

  const close = useCallback(() => setOpen(false), [])
  const toggle = useCallback(() => setOpen(o => { if (!o) setPos(null); return !o }), [])

  // Anchor the menu under (or above, when there's no room) the trigger, clamped
  // to the viewport. Measures the rendered menu so flipping/clamping is accurate.
  const place = useCallback(() => {
    const anchor = anchorRef.current
    if (!anchor) return
    const r = anchor.getBoundingClientRect()
    const menu = menuRef.current
    const menuH = menu?.offsetHeight || 0
    const menuW = menu?.offsetWidth || width
    const gap = 6
    let top = r.bottom + gap
    if (menuH && top + menuH > window.innerHeight - 8) {
      const above = r.top - gap - menuH
      top = above >= 8 ? above : Math.max(8, window.innerHeight - menuH - 8)
    }
    let left = align === 'right' ? r.right - menuW : r.left
    left = Math.max(8, Math.min(left, window.innerWidth - menuW - 8))
    setPos({ top, left })
  }, [align, width])

  useLayoutEffect(() => {
    if (!open) return
    place()
    const onMove = () => place()
    window.addEventListener('scroll', onMove, true)
    window.addEventListener('resize', onMove)
    return () => {
      window.removeEventListener('scroll', onMove, true)
      window.removeEventListener('resize', onMove)
    }
  }, [open, place])

  return (
    <div ref={anchorRef} style={{ position: 'relative', display: 'inline-flex' }}>
      {trigger(toggle, open)}
      {open && createPortal(
        <>
          <div onClick={close} style={{ position: 'fixed', inset: 0, zIndex: 1000 }} />
          <div ref={menuRef} className="card" onClick={close}
            style={{ position: 'fixed', top: pos?.top ?? 0, left: pos?.left ?? 0, zIndex: 1001, minWidth: width, padding: 6, boxShadow: '0 10px 30px rgba(0,0,0,0.22)', visibility: pos ? 'visible' : 'hidden' }}>
            {children}
          </div>
        </>,
        document.body
      )}
    </div>
  )
}

function MenuItem({ icon, label, danger, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{ display: 'flex', width: '100%', alignItems: 'center', gap: 9, padding: '8px 10px', borderRadius: 8, fontSize: 12.5, textAlign: 'left', background: 'transparent', color: danger ? 'var(--danger)' : 'var(--text-soft)' }}
      onMouseEnter={e => { e.currentTarget.style.background = 'var(--surface-2)' }}
      onMouseLeave={e => { e.currentTarget.style.background = 'transparent' }}
    >
      <span style={{ display: 'flex', width: 15 }}>{icon}</span> {label}
    </button>
  )
}

function FilterMenu({ label, value, options, onChange }) {
  const current = options.find(o => o.value === value)?.label || 'All'
  return (
    <Popover width={170} trigger={(toggle, open) => (
      <button className={`btn btn-sm${value !== 'all' ? ' btn-primary' : ''}`} onClick={toggle}>
        {label}: {current} <I.chevronDown style={{ fontSize: 12, opacity: 0.7 }} />
      </button>
    )}>
      {options.map(o => (
        <MenuItem key={o.value} label={o.label} onClick={() => onChange(o.value)}
          icon={value === o.value ? <I.check style={{ color: 'var(--accent)' }} /> : null} />
      ))}
    </Popover>
  )
}

// Permission matrix catalogue (labels/grouping live in the UI; values come from the API)
const PERM_GROUPS = [
  { group: 'Sites', icon: 'globe', items: [
    { k: 'sites.view', l: 'View sites' },
    { k: 'sites.edit', l: 'Edit content' },
    { k: 'sites.publish', l: 'Publish changes' },
    { k: 'sites.delete', l: 'Delete sites' },
  ] },
  { group: 'Clients', icon: 'users', items: [
    { k: 'clients.view', l: 'View clients' },
    { k: 'clients.manage', l: 'Add & edit clients' },
  ] },
  { group: 'Billing', icon: 'card', items: [
    { k: 'billing.view', l: 'View invoices' },
    { k: 'billing.manage', l: 'Manage subscriptions' },
  ] },
  { group: 'Team & domains', icon: 'shield', items: [
    { k: 'team.invite', l: 'Invite members' },
    { k: 'domains.manage', l: 'Connect & manage domains' },
  ] },
]

// ── Invite member modal (NS design, wired to POST /users/invite) ──────────────
function InviteMemberModal({ onClose, onInvited }) {
  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const [role, setRole] = useState('editor')
  const [siteIds, setSiteIds] = useState([])
  const [message, setMessage] = useState('')
  const [require2fa, setRequire2fa] = useState(true)

  const { data: sitesData } = useQuery('sites', () => apiService.get('/sites').then(r => r.data), { staleTime: 5 * 60 * 1000 })
  const sites = sitesData?.sites || []
  const { data: rolesData } = useQuery(['roles'], () => apiService.get('/roles').then(r => r.data))
  const roles = rolesData?.roles || []

  const toggleSite = (id) => setSiteIds(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id])

  const inviteMutation = useMutation(
    (payload) => apiService.post('/users/invite', payload),
    {
      onSuccess: (res) => { toast.success(res.data?.message || 'Invite sent'); onInvited?.(); onClose() },
      onError: (e) => toast.error(e.response?.data?.error || 'Failed to send invite'),
    }
  )

  const submit = () => {
    if (!email.trim()) { toast.error('Enter an email address'); return }
    inviteMutation.mutate({ email: email.trim(), name: name.trim(), role, site_access: siteIds, personal_message: message.trim(), require_2fa: require2fa })
  }

  const multipleEmails = email.includes(',')

  return (
    <NSModal
      icon="send" tone="accent" onClose={onClose} width={620}
      title="Invite team member" subtitle="They'll get an email invite to join your workspace."
      foot={
        <>
          <button className="btn" style={{ marginLeft: 'auto' }} onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={submit} disabled={inviteMutation.isLoading}>
            <I.send /> {inviteMutation.isLoading ? 'Sending…' : 'Send invite'}
          </button>
        </>
      }
    >
      <NSField label="Email address" hint="separate multiple with commas">
        <input className="input" autoFocus value={email} onChange={e => setEmail(e.target.value)} placeholder="name@company.com" />
      </NSField>

      <NSField label="Full name" hint={multipleEmails ? 'ignored when inviting several at once' : 'optional'}>
        <input className="input" value={name} onChange={e => setName(e.target.value)} placeholder="Jane Doe" disabled={multipleEmails} />
      </NSField>

      <NSField label="Role">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {roles.map(r => {
            const on = role === r.slug
            return (
              <button key={r.id} type="button" onClick={() => setRole(r.slug)} style={{
                textAlign: 'left', display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', borderRadius: 11,
                border: `1.5px solid ${on ? 'var(--border-accent)' : 'var(--border)'}`,
                background: on ? 'var(--accent-soft)' : 'var(--surface)', transition: 'all 0.13s ease', cursor: 'pointer',
              }}>
                <span className="icon-tile" style={{ width: 30, height: 30, fontSize: 14, background: on ? 'var(--accent)' : 'var(--surface-2)', color: on ? 'white' : 'var(--text-muted)', border: 'none' }}>
                  {r.slug === 'client' ? <I.user /> : <I.shield />}
                </span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{r.name}</div>
                  <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 2 }}>{r.description}</div>
                </div>
                <span style={{ width: 18, height: 18, borderRadius: '50%', border: `1.5px solid ${on ? 'transparent' : 'var(--border-strong)'}`, background: on ? 'var(--accent)' : 'transparent', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, flexShrink: 0 }}>{on && <I.check />}</span>
              </button>
            )
          })}
        </div>
      </NSField>

      <NSField label="Site access" hint={role === 'admin' ? 'admins can access all sites' : 'choose which sites they can work on'}>
        {role === 'admin' ? (
          <div style={{ fontSize: 12.5, color: 'var(--text-muted)', padding: '10px 12px', border: '1px dashed var(--border-strong)', borderRadius: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
            <I.globe style={{ color: 'var(--accent-3)' }} /> Full access to all {sites.length} sites
          </div>
        ) : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {sites.map(s => <NSChip key={s.id} label={s.name} on={siteIds.includes(s.id)} onClick={() => toggleSite(s.id)} icon="globe" />)}
            {sites.length === 0 && <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>No sites yet</span>}
          </div>
        )}
      </NSField>

      <NSField label="Personal message" hint="optional">
        <textarea className="input" rows={3} style={{ resize: 'vertical', lineHeight: 1.6 }} value={message} onChange={e => setMessage(e.target.value)} placeholder="Add a note to the invitation email…" />
      </NSField>

      <label style={{ display: 'flex', alignItems: 'center', gap: 9, fontSize: 12.5, color: 'var(--text-soft)', cursor: 'pointer' }}>
        <NSTogglePerm on={require2fa} onChange={setRequire2fa} /> Require two-factor authentication for this member
      </label>
    </NSModal>
  )
}

// ── Edit role modal (NS design, wired to /roles/:id) ──────────────────────────
function EditRoleModal({ roleSlug, onClose, onSaved }) {
  const { data } = useQuery(['role', roleSlug], () => apiService.get(`/roles/${roleSlug}`).then(r => r.data))
  const role = data?.role

  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [perms, setPerms] = useState({})
  const [hydrated, setHydrated] = useState(false)

  React.useEffect(() => {
    if (role && !hydrated) {
      setName(role.name); setDescription(role.description || ''); setPerms(role.permissions || {}); setHydrated(true)
    }
  }, [role, hydrated])

  const toggle = (k) => setPerms(p => ({ ...p, [k]: !p[k] }))
  const total = Object.values(perms).filter(Boolean).length

  const saveMutation = useMutation(
    (payload) => apiService.put(`/roles/${roleSlug}`, payload),
    { onSuccess: () => { toast.success('Role saved'); onSaved?.(); onClose() }, onError: (e) => toast.error(e.response?.data?.error || 'Failed to save role') }
  )
  const deleteMutation = useMutation(
    () => apiService.delete(`/roles/${roleSlug}`),
    { onSuccess: () => { toast.success('Role deleted'); onSaved?.(); onClose() }, onError: (e) => toast.error(e.response?.data?.error || 'Failed to delete role') }
  )

  return (
    <NSModal
      icon="shield" tone="accent" onClose={onClose} width={620}
      title={`Edit role · ${role?.name || ''}`} subtitle={`${total} permissions enabled · applies to all members with this role`}
      foot={
        <>
          {!role?.is_system && (
            <button className="btn btn-ghost" style={{ marginRight: 'auto', color: 'var(--danger)' }} onClick={() => deleteMutation.mutate()}><I.trash /> Delete role</button>
          )}
          <button className="btn" style={role?.is_system ? { marginLeft: 'auto' } : undefined} onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={() => saveMutation.mutate({ name, description, permissions: perms })} disabled={saveMutation.isLoading}><I.check /> {saveMutation.isLoading ? 'Saving…' : 'Save role'}</button>
        </>
      }
    >
      <div className="stack-mobile" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <NSField label="Role name">
          <input className="input" value={name} onChange={e => setName(e.target.value)} disabled={role?.is_system} />
        </NSField>
        <NSField label="Members">
          <div className="input" style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--text-muted)' }}>
            <I.users /> {role?.member_count || 0} member{(role?.member_count || 0) === 1 ? '' : 's'}
          </div>
        </NSField>
      </div>

      <NSField label="Description" full>
        <input className="input" value={description} onChange={e => setDescription(e.target.value)} />
      </NSField>

      <NSField label="Permissions">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {PERM_GROUPS.map(g => (
            <div key={g.group}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <Icon name={g.icon} style={{ color: 'var(--text-muted)', fontSize: 14 }} />
                <span className="drawer-section-label" style={{ margin: 0 }}>{g.group}</span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {g.items.map(i => (
                  <div key={i.k} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', border: '1px solid var(--border)', borderRadius: 10, background: 'var(--surface)' }}>
                    <span style={{ flex: 1, fontSize: 12.5, color: 'var(--text-soft)' }}>{i.l}</span>
                    <span style={{ fontSize: 10.5, color: 'var(--text-faint)', fontFamily: 'var(--font-mono)' }}>{i.k}</span>
                    <NSTogglePerm on={!!perms[i.k]} onChange={() => toggle(i.k)} />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </NSField>
    </NSModal>
  )
}

// ── Roles & permissions panel (lists roles, opens Edit role) ──────────────────
function RolesPanel({ onClose, onEdit }) {
  const { data } = useQuery(['roles'], () => apiService.get('/roles').then(r => r.data))
  const roles = data?.roles || []
  return (
    <NSModal icon="shield" tone="accent" onClose={onClose} width={560}
      title="Roles & permissions" subtitle="Define what each role can do across the workspace."
      foot={<button className="btn" style={{ marginLeft: 'auto' }} onClick={onClose}>Close</button>}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {roles.map(r => (
          <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', border: '1px solid var(--border)', borderRadius: 11, background: 'var(--surface)' }}>
            <span className="icon-tile" style={{ width: 32, height: 32, fontSize: 14 }}><I.shield /></span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600 }}>{r.name}{r.is_system && <span style={{ fontSize: 10.5, color: 'var(--text-faint)', marginLeft: 6 }}>system</span>}</div>
              <div className="truncate" style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 2 }}>{r.description}</div>
            </div>
            <span style={{ fontSize: 11.5, color: 'var(--text-muted)', flexShrink: 0 }}>{r.member_count} member{r.member_count === 1 ? '' : 's'}</span>
            <button className="btn btn-sm" onClick={() => onEdit(r.slug)}>Edit role</button>
          </div>
        ))}
      </div>
    </NSModal>
  )
}

// Local toggle (avoids clashing with the file's existing NSToggle helper)
function NSTogglePerm({ on, onChange }) {
  return (
    <button type="button" onClick={(e) => { e.stopPropagation(); onChange(!on) }} style={{
      width: 34, height: 19, borderRadius: 999, flexShrink: 0,
      background: on ? 'var(--accent)' : 'var(--surface-2)', position: 'relative', transition: 'background 0.15s ease', border: '1px solid var(--border)',
    }}>
      <span style={{ position: 'absolute', top: 1, left: on ? 16 : 1, width: 15, height: 15, borderRadius: '50%', background: 'white', transition: 'left 0.15s ease', boxShadow: '0 1px 2px rgba(0,0,0,0.3)' }} />
    </button>
  )
}

const inputStyle = {
  width: '100%',
  padding: '7px 10px',
  border: '1px solid var(--border)',
  borderRadius: 8,
  background: 'var(--surface)',
  color: 'var(--text)',
  fontSize: 13,
  outline: 'none',
  boxSizing: 'border-box',
}

const UserManager = () => {
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedRole, setSelectedRole] = useState('all')
  const [selectedStatus, setSelectedStatus] = useState('all')
  const [showInvite, setShowInvite] = useState(false)
  const [showRoles, setShowRoles] = useState(false)
  const [editRoleSlug, setEditRoleSlug] = useState(null)
  const [editingUser, setEditingUser] = useState(null)
  const [resetPasswordUser, setResetPasswordUser] = useState(null)
  const queryClient = useQueryClient()

  const { data: usersData, isLoading, error } = useQuery(
    ['users'],
    () => apiService.get('/users'),
    {
      staleTime: 0,
      refetchOnWindowFocus: true,
      retry: 1
    }
  )

  const { data: plansRaw } = useQuery(
    'billing-plans',
    () => apiService.get('/billing/plans').then(r => r.data),
    { staleTime: 5 * 60 * 1000 }
  )

  const { data: rolesData } = useQuery(['roles'], () => apiService.get('/roles').then(r => r.data), { staleTime: 5 * 60 * 1000 })
  const roles = rolesData?.roles || []

  // Transform API data to match expected format
  const users = usersData?.data || usersData

  const updateUserMutation = useMutation(
    ({ id, ...userData }) => apiService.put(`/users/${id}`, userData),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('users')
        setEditingUser(null)
        toast.success('User updated successfully')
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to update user')
      }
    }
  )

  const resetPasswordMutation = useMutation(
    ({ id, password }) => apiService.patch(`/users/${id}/password`, { password }),
    {
      onSuccess: () => {
        setResetPasswordUser(null)
        toast.success('Password updated successfully')
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Failed to update password')
      }
    }
  )

  const deleteUserMutation = useMutation(
    (userId) => apiService.delete(`/users/${userId}`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('users')
        toast.success('User deleted successfully')
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to delete user')
      }
    }
  )

  const reset2FAMutation = useMutation(
    (userId) => apiService.delete(`/users/${userId}/2fa`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('users')
        setEditingUser(prev => prev ? { ...prev, totp_enabled: 0 } : prev)
        toast.success('2FA reset successfully')
      },
      onError: () => {
        toast.error('Failed to reset 2FA')
      }
    }
  )

  const forceMfaMutation = useMutation(
    (userId) => apiService.put(`/users/${userId}/force-mfa`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['users'])
        setEditingUser(prev => prev ? { ...prev, mfa_required: 1 } : prev)
        toast.success('MFA setup will be required on next login')
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Failed to force MFA')
      }
    }
  )

  const cancelForceMfaMutation = useMutation(
    (userId) => apiService.delete(`/users/${userId}/force-mfa`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['users'])
        setEditingUser(prev => prev ? { ...prev, mfa_required: 0 } : prev)
        toast.success('Forced MFA requirement removed')
      },
      onError: () => {
        toast.error('Failed to remove forced MFA')
      }
    }
  )

  // Transform API data to match expected format with proper status mapping
  const transformedUsers = users ? users.map(user => ({
    ...user,
    status: user.is_active ? 'active' : 'inactive',
    is_verified: true // Default to true for now, add email verification later if needed
  })) : []

  // Real data only. On error the list stays empty and a banner is shown
  // (see below) — never fabricated users.
  const displayUsers = transformedUsers

  // Client-side filtering with useMemo for performance
  const filteredUsers = useMemo(() => {
    return displayUsers.filter(user => {
      const matchesSearch = searchTerm === '' ||
        user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.name?.toLowerCase().includes(searchTerm.toLowerCase())
      const matchesRole = selectedRole === 'all' || user.role === selectedRole
      const matchesStatus = selectedStatus === 'all' || user.status === selectedStatus
      return matchesSearch && matchesRole && matchesStatus
    })
  }, [displayUsers, searchTerm, selectedRole, selectedStatus])

  const handleDeleteUser = async (user) => {
    const ok = await nsConfirm({
      tone: 'danger',
      icon: 'user',
      title: `Delete ${user.name}?`,
      message: `${user.email} will be permanently removed, along with all of their data, associated sites and settings.`,
      detail: 'This cannot be undone',
      requireText: user.email,
      confirmLabel: 'Delete user',
    })
    if (ok) deleteUserMutation.mutate(user.id)
  }

  const handleToggleStatus = (user) => {
    const newStatus = user.status === 'active' ? 'inactive' : 'active'
    updateUserMutation.mutate({
      id: user.id,
      status: newStatus
    })
  }

  const handleReset2FA = async (user) => {
    const ok = await nsConfirm({
      tone: 'warning',
      icon: 'shield',
      title: `Reset 2FA for ${user.name}?`,
      message: `${user.email} will have two-factor authentication removed and can sign in with just their password. Use this only when they have lost access to their authenticator.`,
      detail: 'They will need to set up 2FA again themselves',
      confirmLabel: 'Reset 2FA',
    })
    if (ok) reset2FAMutation.mutate(user.id)
  }

  const ResetPasswordForm = ({ user, onSubmit, onCancel, isLoading }) => {
    const [password, setPassword] = useState('')
    const [confirm, setConfirm] = useState('')

    const handleSubmit = (e) => {
      e.preventDefault()
      if (password.length < 6) {
        toast.error('Password must be at least 6 characters')
        return
      }
      if (password !== confirm) {
        toast.error('Passwords do not match')
        return
      }
      onSubmit({ password })
    }

    return (
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: 0 }}>
          Set a new password for <span style={{ fontWeight: 600, color: 'var(--text)' }}>{user.name}</span> ({user.email}).
        </p>
        <div>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: 'var(--text-muted)', marginBottom: 6 }}>New Password</label>
          <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} style={inputStyle} placeholder="Enter new password" autoFocus />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: 'var(--text-muted)', marginBottom: 6 }}>Confirm Password</label>
          <input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} style={inputStyle} placeholder="Confirm new password" />
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, paddingTop: 4 }}>
          <button type="button" className="btn btn-ghost" onClick={onCancel}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={isLoading}>
            {isLoading ? 'Saving...' : 'Set Password'}
          </button>
        </div>
      </form>
    )
  }

  const UserForm = ({ user, onSubmit, onCancel }) => {
    const [formData, setFormData] = useState({
      name: user?.name || '',
      email: user?.email || '',
      role: user?.role || 'client',
      status: user?.status || 'active',
      subscription_plan: user?.subscription_plan || 'basic',
      password: '',
      confirmPassword: ''
    })

    const { data: plansRaw } = useQuery(
      'billing-plans',
      () => apiService.get('/billing/plans').then(r => r.data),
      { staleTime: 5 * 60 * 1000 }
    )

    const planOptions = plansRaw
      ? Object.entries(plansRaw).map(([id, plan]) => ({ id, name: plan.name || id, price: plan.price }))
      : [{ id: 'basic', name: 'Basic', price: null }, { id: 'pro', name: 'Pro', price: null }, { id: 'enterprise', name: 'Enterprise', price: null }]

    const handleSubmit = (e) => {
      e.preventDefault()
      if (!user && formData.password !== formData.confirmPassword) {
        toast.error('Passwords do not match')
        return
      }
      if (user && formData.password && formData.password !== formData.confirmPassword) {
        toast.error('Passwords do not match')
        return
      }
      const { confirmPassword, ...submitData } = formData
      if (user && !submitData.password) delete submitData.password
      onSubmit(submitData)
    }

    const labelStyle = { display: 'block', fontSize: 12, fontWeight: 500, color: 'var(--text-muted)', marginBottom: 6 }

    return (
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div>
          <label style={labelStyle}>Name</label>
          <input type="text" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} style={inputStyle} required />
        </div>
        <div>
          <label style={labelStyle}>Email</label>
          <input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} style={inputStyle} required />
        </div>
        <div className="stack-mobile" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div>
            <label style={labelStyle}>Role</label>
            <NSSelect
              value={formData.role}
              onChange={(e) => setFormData({...formData, role: e.target.value})}
              options={[
                { value: 'client', label: 'Client' },
                { value: 'admin', label: 'Admin' },
                { value: 'moderator', label: 'Moderator' },
              ]}
            />
          </div>
          <div>
            <label style={labelStyle}>Status</label>
            <NSSelect
              value={formData.status}
              onChange={(e) => setFormData({...formData, status: e.target.value})}
              options={[
                { value: 'active', label: 'Active' },
                { value: 'inactive', label: 'Inactive' },
                { value: 'banned', label: 'Banned' },
              ]}
            />
          </div>
        </div>
        {formData.role === 'client' && (
          <div>
            <label style={labelStyle}>Subscription Plan</label>
            <NSSelect
              value={formData.subscription_plan}
              onChange={(e) => setFormData({...formData, subscription_plan: e.target.value})}
              options={planOptions.map(p => ({ value: p.id, label: `${p.name}${p.price != null ? ` — £${p.price}/mo` : ''}` }))}
            />
          </div>
        )}
        {!user && (
          <>
            <div>
              <label style={labelStyle}>Password</label>
              <input type="password" value={formData.password} onChange={(e) => setFormData({...formData, password: e.target.value})} style={inputStyle} required={!user} />
            </div>
            <div>
              <label style={labelStyle}>Confirm Password</label>
              <input type="password" value={formData.confirmPassword} onChange={(e) => setFormData({...formData, confirmPassword: e.target.value})} style={inputStyle} required={!user} />
            </div>
          </>
        )}
        {user && (
          <div style={{ paddingTop: 12, borderTop: '1px solid var(--border)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <p style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)', margin: 0 }}>Two-Factor Authentication</p>
                <p style={{ fontSize: 11.5, color: 'var(--text-muted)', margin: '2px 0 0' }}>
                  {user.totp_enabled ? '2FA is currently enabled for this user' : '2FA is not enabled'}
                </p>
              </div>
              {user.totp_enabled ? (
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={() => reset2FAMutation.mutate(user.id)}
                  disabled={reset2FAMutation.isLoading}
                  style={{ color: '#ef4444', borderColor: 'rgba(239,68,68,.3)' }}
                >
                  {reset2FAMutation.isLoading ? 'Resetting…' : 'Reset 2FA'}
                </button>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {user.mfa_required ? (
                    <button
                      type="button"
                      className="btn btn-ghost"
                      onClick={() => cancelForceMfaMutation.mutate(user.id)}
                      disabled={cancelForceMfaMutation.isLoading}
                      style={{ color: '#f59e0b' }}
                    >
                      {cancelForceMfaMutation.isLoading ? 'Removing…' : 'Cancel Requirement'}
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="btn btn-ghost"
                      onClick={() => forceMfaMutation.mutate(user.id)}
                      disabled={forceMfaMutation.isLoading}
                      style={{ color: 'var(--accent)' }}
                    >
                      {forceMfaMutation.isLoading ? 'Forcing…' : 'Force MFA Setup'}
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, paddingTop: 4 }}>
          <button type="button" className="btn btn-ghost" onClick={onCancel}>Cancel</button>
          <button type="submit" className="btn btn-primary">
            {user ? 'Update' : 'Create'} User
          </button>
        </div>
      </form>
    )
  }

  if (isLoading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 240 }}>
        <LoadingSpinner size="lg" text="Loading users..." />
      </div>
    )
  }

  // ── Stats (prototype set) ──────────────────────────────────────────────
  const total = displayUsers.length
  const clientGuests = displayUsers.filter(u => u.role === 'client').length
  const members = total - clientGuests
  const twoFa = displayUsers.filter(u => u.totp_enabled).length
  const pendingInvites = displayUsers.filter(u => !u.last_login).length // approx: never logged in

  const ROLE_OPTIONS = [
    { value: 'all', label: 'All' },
    ...Array.from(new Set(displayUsers.map(u => u.role).filter(Boolean))).map(r => ({ value: r, label: roleLabel(r) })),
  ]
  const STATUS_OPTIONS = [
    { value: 'all', label: 'All' },
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' },
  ]

  const STAT_TILES = [
    { label: 'Members', value: members, icon: 'users', tone: '' },
    { label: 'Client guests', value: clientGuests, icon: 'user', tone: 'info' },
    { label: '2FA enabled', value: `${twoFa} / ${total}`, icon: 'shield', tone: 'success' },
    { label: 'Pending invites', value: pendingInvites, icon: 'send', tone: 'warning' },
  ]

  const COLS = '2fr 1.4fr 1fr 1fr 0.8fr 1.2fr auto'
  const parseAccess = (s) => (s ? String(s).split('||').filter(Boolean) : [])

  return (
    <>
      {/* ── Header ─── */}
      <div className="page-header">
        <div className="page-title">
          <div className="eyebrow">Workspace · Team</div>
          <h1>Team &amp; permissions</h1>
          <div className="subtitle">{members} internal · {clientGuests} client guests · {twoFa}/{total} with 2FA</div>
        </div>
        <div className="flex gap-2 items-center">
          <button className="btn" onClick={() => setShowRoles(true)}><I.shield /> Roles &amp; permissions</button>
          <button className="btn btn-primary" onClick={() => setShowInvite(true)}><I.plus /> Invite member</button>
        </div>
      </div>

      <div className="page-body">
        {/* ── Stats ─── */}
        <div className="bento" style={{ gridTemplateColumns: 'repeat(4, 1fr)', gap: 14 }}>
          {STAT_TILES.map(s => (
            <div key={s.label} className="card card-pad-sm" style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
              <span className={`icon-tile${s.tone ? ' ' + s.tone : ''}`}><Icon name={s.icon} /></span>
              <div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{s.label}</div>
                <div style={{ fontSize: 22, fontWeight: 600, letterSpacing: '-0.02em', fontFamily: 'var(--font-mono)', marginTop: 2 }}>{s.value}</div>
              </div>
            </div>
          ))}
        </div>

        {/* ── Filter bar ─── */}
        <div className="filter-bar">
          <I.search style={{ color: 'var(--text-muted)', fontSize: 16, flexShrink: 0 }} />
          <input className="input" placeholder="Search team…" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
          <div className="vdiv" style={{ height: 22 }} />
          <FilterMenu label="Role" value={selectedRole} options={ROLE_OPTIONS} onChange={setSelectedRole} />
          <FilterMenu label="Status" value={selectedStatus} options={STATUS_OPTIONS} onChange={setSelectedStatus} />
        </div>

        {/* ── Load error ─── */}
        {error && (
          <div className="card card-pad" style={{ border: '1.5px solid var(--danger)', background: 'var(--danger-soft)', color: 'var(--danger)', display: 'flex', alignItems: 'center', gap: 10, fontSize: 13 }}>
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
            <span>Couldn’t load team members. {error.response?.data?.error || 'Please retry.'}</span>
            <button className="btn btn-sm" style={{ marginLeft: 'auto' }} onClick={() => queryClient.invalidateQueries('users')}>Retry</button>
          </div>
        )}

        {/* ── Team table ─── */}
        <div className="sites-table">
          <div className="row header" style={{ gridTemplateColumns: COLS }}>
            <div>Member</div><div>Role</div><div>2FA</div><div>Status</div><div>Last seen</div><div>Access</div><div></div>
          </div>
          {filteredUsers.length === 0 ? (
            <div className="row" style={{ gridTemplateColumns: '1fr' }}>
              <div style={{ textAlign: 'center', padding: '28px 0', color: 'var(--text-faint)', fontSize: 13 }}>No team members match your filters</div>
            </div>
          ) : filteredUsers.map(user => {
            const access = parseAccess(user.site_access)
            const isClient = user.role === 'client'
            return (
              <div className="row" key={user.id} style={{ gridTemplateColumns: COLS }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                  <div className={`avatar avatar-md avatar-grad-${gradIndex(user.id)}`}>{getInitials(user.name || user.email)}</div>
                  <div style={{ minWidth: 0 }}>
                    <div className="truncate" style={{ color: 'var(--text)', fontWeight: 500 }}>{user.name || 'Unnamed'}</div>
                    <div className="truncate" style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>{user.email}</div>
                  </div>
                </div>
                <div>
                  <span className="badge">{isClient ? <I.user /> : <I.shield />} {roleLabel(user.role)}</span>
                </div>
                <div>
                  {user.totp_enabled
                    ? <span className="badge badge-success"><I.check /> on</span>
                    : <span className="badge badge-warning">off</span>}
                </div>
                <div>
                  <span className={`badge badge-${user.status === 'active' ? 'success' : 'info'}`}><span className="badge-dot" /> {user.status}</span>
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{relativeTime(user.last_login)}</div>
                <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', alignContent: 'center' }}>
                  {user.role === 'admin'
                    ? <span className="badge" style={{ fontSize: 10 }}>All sites</span>
                    : access.length > 0
                      ? <>
                          {access.slice(0, 2).map(a => <span key={a} className="badge truncate" style={{ fontSize: 10, maxWidth: 92 }}>{a}</span>)}
                          {access.length > 2 && <span className="badge" style={{ fontSize: 10 }}>+{access.length - 2}</span>}
                        </>
                      : <span style={{ fontSize: 11.5, color: 'var(--text-faint)' }}>—</span>}
                </div>
                <div style={{ display: 'flex', gap: 4 }}>
                  <button className="site-action" title="Edit member" onClick={() => setEditingUser(user)}><I.settings /></button>
                  <Popover align="right" width={200} trigger={(toggle) => (
                    <button className="site-action" title="More" onClick={toggle}><I.more /></button>
                  )}>
                    <MenuItem icon={<I.edit />} label="Edit member" onClick={() => setEditingUser(user)} />
                    <MenuItem icon={<I.shield />} label="Reset password" onClick={() => setResetPasswordUser(user)} />
                    <MenuItem icon={user.status === 'active' ? <I.x /> : <I.check />} label={user.status === 'active' ? 'Deactivate' : 'Activate'} onClick={() => handleToggleStatus(user)} />
                    {!user.totp_enabled && (
                      <MenuItem icon={<I.shield />} label={user.mfa_required ? 'Cancel forced MFA' : 'Force MFA setup'} onClick={() => user.mfa_required ? cancelForceMfaMutation.mutate(user.id) : forceMfaMutation.mutate(user.id)} />
                    )}
                    {!!user.totp_enabled && (
                      <MenuItem icon={<I.shield />} label="Reset 2FA" onClick={() => handleReset2FA(user)} />
                    )}
                    <div className="divider" style={{ margin: '4px 0' }} />
                    <MenuItem icon={<I.trash />} label="Delete member" danger onClick={() => handleDeleteUser(user)} />
                  </Popover>
                </div>
              </div>
            )
          })}
        </div>

        {/* ── Role cards ─── */}
        {roles.length > 0 && (
          <div className="bento" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(min(240px, 100%), 1fr))', gap: 14 }}>
            {roles.map(r => (
              <div key={r.id} className="card card-pad" style={{ display: 'flex', flexDirection: 'column', gap: 10, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                  <span className="icon-tile" style={{ flexShrink: 0 }}><I.shield /></span>
                  <div style={{ minWidth: 0 }}>
                    <div className="truncate" style={{ fontSize: 14, fontWeight: 600 }}>{r.name}</div>
                    <div style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>{r.member_count} member{r.member_count === 1 ? '' : 's'}</div>
                  </div>
                </div>
                <div style={{ fontSize: 12.5, color: 'var(--text-soft)', lineHeight: 1.5 }}>{r.description}</div>
                <button className="btn btn-sm" style={{ marginTop: 'auto', width: '100%' }} onClick={() => setEditRoleSlug(r.slug)}>Edit role</button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Reset Password Modal */}
      <Modal isOpen={!!resetPasswordUser} onClose={() => setResetPasswordUser(null)} title="Reset Password">
        {resetPasswordUser && (
          <ResetPasswordForm
            user={resetPasswordUser}
            onSubmit={({ password }) => resetPasswordMutation.mutate({ id: resetPasswordUser.id, password })}
            onCancel={() => setResetPasswordUser(null)}
            isLoading={resetPasswordMutation.isLoading}
          />
        )}
      </Modal>

      {/* Edit User Modal */}
      <Modal isOpen={!!editingUser} onClose={() => setEditingUser(null)} title="Edit User">
        {editingUser && (
          <UserForm
            user={editingUser}
            onSubmit={(userData) => updateUserMutation.mutate({ id: editingUser.id, ...userData })}
            onCancel={() => setEditingUser(null)}
          />
        )}
      </Modal>

      {/* Invite member */}
      {showInvite && (
        <InviteMemberModal
          onClose={() => setShowInvite(false)}
          onInvited={() => queryClient.invalidateQueries(['users'])}
        />
      )}

      {/* Roles & permissions */}
      {showRoles && (
        <RolesPanel
          onClose={() => setShowRoles(false)}
          onEdit={(slug) => { setShowRoles(false); setEditRoleSlug(slug) }}
        />
      )}
      {editRoleSlug && (
        <EditRoleModal
          roleSlug={editRoleSlug}
          onClose={() => setEditRoleSlug(null)}
          onSaved={() => queryClient.invalidateQueries(['roles'])}
        />
      )}
    </>
  )
}

export default UserManager
